import React, { useState } from 'react';
import styled from 'styled-components';
import TrueImpactLoading from '../TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from '../../design-system/components/TrueImpactButton';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from '../../design-system/settings/variables/Colors';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ReportingTaskState, reportingTaskText } from './sectionText';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { mergeClassNames } from '../../utils/mergeClassNames';

const StyledDiv = styled.div`
  display: flex;
  max-width: 492px;
  border-radius: 4px;
  gap: ${SPACERS.small};
`;

const StyledAccordion = styled(Accordion)`
  border: 1px solid ${COLORS.grayLight};
  border-radius: 4px;
  .panel-header {
    border-radius: 4px;
    padding: ${SPACERS.small} ${SPACERS.medium};
  }
  .panel-header.active-panel-header {
    background-color: ${COLORS.ctaPrimaryBackground};
  }
  &.MuiAccordion-root {
    flex: 1;
    box-shadow: none;
    color: ${COLORS.blackBase};
    border-radius: 4px;
  }
  .MuiAccordionSummary-content,
  .label {
    align-items: center;
  }
  .label {
    margin-bottom: 0;
  }
  .section-icon {
    font-size: 24px;
    padding: 4px;
  }
  .summary {
    display: flex;
    padding: ${SPACERS.medium};
    background-color: ${COLORS.greyLightest};
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: ${SPACERS.small};
  }
  .MuiAccordionSummary-content.Mui-expanded {
    margin: 0;
  }
  &.MuiAccordion-root.Mui-expanded {
    margin: 0;
  }
  &.MuiAccordion-root:before {
    content: none;
  }
  .MuiAccordionSummary-content {
    gap: ${SPACERS.tiny};
  }
  .MuiIconButton-label,
  .icon-wrapper {
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .MuiIconButton-edgeEnd {
    margin-right: 0;
  }
  .MuiIconButton-root {
    padding: 0;
    font-size: 24px;
  }
  .icon-wrapper {
  }
`;

const StatusBar = styled.div`
  position: relative;
  min-width: 8px;
  border-radius: 50px;
  overflow: hidden;
  .background {
    background-color: ${COLORS.greyLight};
    height: 100%;
    position: relative;
  }
  .progress {
    &.partial,
    &.completed {
      background-color: ${COLORS.success};
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
      border-radius: 50px;
    }
    &.partial {
      height: 50%;
    }
    &.completed {
      height: 100%;
    }
  }
`;

export function AngleDown() {
  return <FontAwesomeIcon icon={faAngleDown} />;
}

export interface SectionStatusEventProps {
  isLoading: boolean;
  sectionName: string;
  icon: IconProp;
  isNewReport: boolean;
  isLastViewedSection: boolean;
  sectionId: FeedbackSectionId;
  statusMessage?: string;
  button?: {
    text: string;
    onClick: () => unknown;
  };
  state: ReportingTaskState;
}

const SectionStatus: React.FC<SectionStatusEventProps> = ({
  isLoading,
  sectionId,
  isNewReport,
  isLastViewedSection,
  sectionName,
  icon,
  statusMessage,
  button,
  state,
}) => {
  const [open, setOpen] = useState<boolean>(isNewReport || isLastViewedSection);
  const handleToggle = () => setOpen(o => !o);
  return (
    <StyledDiv>
      <StatusBar onClick={handleToggle}>
        <div className="background"></div>
        <div
          className={`progress ${
            state === 'STARTED' || state === 'LAST_WORKED_ON'
              ? 'partial'
              : state === 'COMPLETED'
              ? 'completed'
              : null
          }`}
        ></div>
      </StatusBar>
      <StyledAccordion
        defaultExpanded={open}
        onClick={handleToggle}
        className={open ? 'active' : ''}
      >
        <AccordionSummary
          expandIcon={<AngleDown />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          className={mergeClassNames(
            'panel-header',
            isLastViewedSection && 'active-panel-header',
          )}
        >
          <div className="icon-wrapper">
            <FontAwesomeIcon icon={icon} className="section-icon" />
          </div>
          <TrueImpactText variant="heading-subheading" className="label">
            {sectionName}
          </TrueImpactText>
        </AccordionSummary>
        <AccordionDetails className="summary">
          <TrueImpactText variant="body" align="center">
            {reportingTaskText[sectionId][state]}
            {statusMessage}
          </TrueImpactText>
          {button && (
            <TrueImpactButton
              variant="primary-large"
              onClick={isLoading ? undefined : button.onClick}
            >
              {isLoading ? (
                <TrueImpactLoading
                  marginBottom={0}
                  marginTop={0}
                  singleLine={true}
                />
              ) : (
                button.text
              )}
            </TrueImpactButton>
          )}
        </AccordionDetails>
      </StyledAccordion>
    </StyledDiv>
  );
};

export default SectionStatus;
